// src/pages/About.js
import React from "react";
import { Link } from "react-router-dom";
import "../index.css";
import useSEO from "../seo/useSEO";

export default function About() {
useSEO({
    title: "À propos | Maxime Huylebroeck, photographe à Nancy — HLBK",
    description:
      "Découvrez Maxime Huylebroeck et HLBK, photographe basé à Tomblaine près de Nancy pour particuliers et professionnels, dans le Grand Est.",
    canonical: "https://www.hlbk.fr/about",
    image: "https://www.hlbk.fr/images/og-default.jpg",
  });

  return (
    <main className="about-page">

      {/* =====================================================
          HERO
      ====================================================== */}
      <section className="about-hero">
        <div className="about-container about-hero-inner">

          <p className="about-kicker">HLBK — Maxime Huylebroeck</p>

          <h1>
            Derrière HLBK,
            <span> il y a avant tout un regard.</span>
          </h1>

          <p className="about-hero-lead">
            Photographe et directeur artistique de formation, je photographie
            les personnes, les histoires et les savoir-faire avec une même
            envie : créer des images qui ont quelque chose à raconter.
          </p>

          <div className="about-hero-actions">
            <Link to="/contact" className="btn-cta btn-cta-red">
              Parler de votre projet
            </Link>

            <a href="#about-story" className="about-text-link">
              Découvrir mon approche ↓
            </a>
          </div>

        </div>
      </section>


      {/* =====================================================
          PRÉSENTATION
      ====================================================== */}
      <section id="about-story" className="about-section">
        <div className="about-container about-story-grid">

          <div className="about-story-image">
            <img
              src="/images/maxime-hlbk.jpg"
              alt="Maxime, photographe HLBK"
              loading="lazy"
            />
          </div>

          <div className="about-story-content">

            <p className="about-kicker">Derrière l'appareil</p>

            <h2>Je suis Maxime.</h2>

            <p className="about-story-intro">
              Photographe basé à Tomblaine, près de Nancy, j'accompagne
              particuliers et professionnels dans le Grand Est et bien au-delà.
            </p>

            <p>
  Ma formation de directeur artistique influence naturellement ma
  manière de photographier. Je ne pense pas seulement à une belle
  image, mais aussi à ce qu'elle raconte, à sa composition, à sa
  lumière et à l'émotion qu'elle transmet.
</p>

<p>
  Cette approche m'a également amené à développer{" "}
  <strong>Maxime H</strong>, mon univers photographique dédié à la
  gastronomie et aux métiers de bouche. Une spécialisation qui nourrit
  mon regard sur la lumière, les matières, les gestes et le savoir-faire.
</p>

<p>
  <a
    href="https://maximeh.fr"
    target="_blank"
    rel="noopener noreferrer"
    className="about-text-link"
  >
    Découvrir Maxime H ↗
  </a>
</p>

<p>
  Qu'il s'agisse d'un mariage, d'un portrait, d'une famille, du geste
  d'un artisan ou de l'univers d'une entreprise, je recherche toujours
  la même chose : <strong>l'image juste.</strong>
</p>

<p>
  Celle qui paraît évidente, naturelle et qui continuera d'avoir
  du sens longtemps après la prise de vue.
</p>

          </div>
        </div>
      </section>


      {/* =====================================================
          VISION
      ====================================================== */}
      <section className="about-section about-vision">
        <div className="about-container about-vision-inner">

          <p className="about-kicker">Ma vision</p>

          <h2>
            Je ne cherche pas simplement
            <span> à faire de belles photos.</span>
          </h2>

          <p className="about-vision-lead">
            Je préfère celles qui ont quelque chose à raconter.
          </p>

          <div className="about-vision-text">
            <p>
              Un regard entre deux personnes. Une lumière qui traverse une
              pièce. Un geste que l'on n'avait pas remarqué. L'énergie d'une
              équipe. Le savoir-faire derrière un métier.
            </p>

            <p>
              Mon travail consiste à observer, comprendre et trouver la bonne
              manière de mettre tout cela en image.
            </p>
          </div>

        </div>
      </section>


      {/* =====================================================
          DEUX UNIVERS
      ====================================================== */}
      <section className="about-section about-universes">
        <div className="about-container">

          <div className="about-section-heading">
            <p className="about-kicker">Deux univers</p>

            <h2>Une même exigence. Deux façons de raconter.</h2>

            <p>
              HLBK accompagne aussi bien les histoires personnelles que
              l'image des entreprises.
            </p>
          </div>


          <div className="about-universe-grid">

            {/* PARTICULIERS */}
            <Link
              to="/services"
              className="about-universe-card about-universe-private"
            >
              <div className="about-universe-overlay" />

              <div className="about-universe-content">
                <span>01</span>

                <p className="about-universe-label">Pour vous</p>

                <h3>Particuliers</h3>

                <p>
                  Portraits, familles, mariages et événements.
                  Des images vivantes et sincères pour raconter
                  ce qui compte vraiment.
                </p>

                <strong>Découvrir les prestations →</strong>
              </div>
            </Link>


            {/* PROFESSIONNELS */}
            <Link
              to="/professionnels"
              className="about-universe-card about-universe-pro"
            >
              <div className="about-universe-overlay" />

              <div className="about-universe-content">
                <span>02</span>

                <p className="about-universe-label">Pour votre activité</p>

                <h3>Professionnels</h3>

                <p>
                  Portraits, reportages, événements et communication.
                  Des images pensées pour valoriser votre entreprise,
                  vos équipes et votre savoir-faire.
                </p>

                <strong>Découvrir l'univers professionnel →</strong>
              </div>
            </Link>

          </div>
        </div>
      </section>


      {/* =====================================================
          MÉTHODE
      ====================================================== */}
      <section className="about-section about-process">
        <div className="about-container">

          <div className="about-section-heading">
            <p className="about-kicker">Ma façon de travailler</p>
            <h2>Simple, humaine et exigeante.</h2>
          </div>


          <div className="about-process-grid">

            <article className="about-process-item">
              <span>01</span>
              <h3>Comprendre</h3>
              <p>
                Avant de photographier, j'écoute. Nous échangeons sur vos
                envies, votre projet et ce que les images doivent transmettre.
              </p>
            </article>


            <article className="about-process-item">
              <span>02</span>
              <h3>Photographier</h3>
              <p>
                Je vous guide lorsque c'est nécessaire et sais aussi me faire
                oublier lorsque l'instant doit simplement se produire.
              </p>
            </article>


            <article className="about-process-item">
              <span>03</span>
              <h3>Travailler</h3>
              <p>
                Chaque reportage est sélectionné et travaillé avec attention :
                lumière, couleurs, contraste et cohérence.
              </p>
            </article>


            <article className="about-process-item">
              <span>04</span>
              <h3>Livrer</h3>
              <p>
                Vos photographies sont livrées en haute définition dans une
                galerie privée, prêtes à être conservées, imprimées ou partagées.
              </p>
            </article>

          </div>
        </div>
      </section>


      {/* =====================================================
          VALEURS
      ====================================================== */}
      <section className="about-section about-values">
        <div className="about-container">

          <div className="about-values-heading">
            <p className="about-kicker">Ce qui guide mon travail</p>

            <h2>
              Humain.
              <br />
              Regard.
              <br />
              Exigence.
            </h2>
          </div>


          <div className="about-values-list">

            <article>
              <span>01</span>

              <div>
                <h3>Humain</h3>
                <p>
                  Parce qu'une bonne photographie commence presque toujours
                  par une relation de confiance.
                </p>
              </div>
            </article>


            <article>
              <span>02</span>

              <div>
                <h3>Regard</h3>
                <p>
                  Parce que deux photographes placés au même endroit ne
                  raconteront jamais exactement la même histoire.
                </p>
              </div>
            </article>


            <article>
              <span>03</span>

              <div>
                <h3>Exigence</h3>
                <p>
                  Parce que la lumière, le cadrage et les détails comptent
                  autant que le moment photographié.
                </p>
              </div>
            </article>

          </div>
        </div>
      </section>


      {/* =====================================================
          CTA FINAL
      ====================================================== */}
      <section className="about-final">
        <div className="about-container about-final-inner">

          <p className="about-kicker">Votre histoire commence ici</p>

          <h2>
            Et si on racontait
            <span> la vôtre ?</span>
          </h2>

          <p>
            Un mariage, une famille, un portrait ou l'image de votre
            entreprise : tout commence simplement par une conversation.
          </p>

          <Link to="/contact" className="btn-cta btn-cta-red">
            Parler de votre projet
          </Link>

        </div>
      </section>

    </main>
  );
}